import { forwardRef, type ButtonHTMLAttributes } from "react"
import { cn } from "../../lib/utils"
import {
  BUTTON_COLOR_CLASS,
  BUTTON_INTERACTION_CLASS,
  type ButtonTone,
  type ButtonVariant,
} from "./button/base"

/** Step on the shared v2 control ladder the visible box sits on. */
export type IconButtonSize = "2xs" | "xs" | "sm" | "md"

/** The same three types as `Button`; `borderless` is the default for a lone glyph. */
export type IconButtonVariant = ButtonVariant

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** `borderless` (default) for toolbar and dismiss glyphs, `secondary` for a standalone control, `primary` for a filled disc. */
  variant?: IconButtonVariant
  tone?: ButtonTone
  /** Toggled-on look (for example, an engaged filter). Paints the pressed colours and sets `aria-pressed`. */
  active?: boolean
  /**
   * Visible box on the v2 control ladder: `2xs` 24px, `xs` 28px, `sm` 32px,
   * `md` 36px. Defaults to `md`. Every step keeps the same 40px hit area.
   */
  size?: IconButtonSize
}

/**
 * The visible circle paired with the pseudo-element that lifts every step back
 * to a 40px hit area. Shrinking the glyph must not shrink the target, so the
 * smaller steps grow `::before` instead — 24px, the WCAG 2.2 floor, still hits
 * at 40.
 */
const SIZE_CLASS: Record<IconButtonSize, string> = {
  "2xs": "size-6 before:-inset-2",
  xs: "size-7 before:-inset-1.5",
  sm: "size-8 before:-inset-1",
  md: "size-9 before:-inset-0.5",
}

/**
 * `relative` (from the shared classes) only — never `overflow-hidden`:
 * clipping would also clip the `::before` that holds the 40px hit area open.
 */
const SHAPE_CLASS = "shrink-0 rounded-full before:absolute before:content-['']"

/** Every variant × tone × size string, merged once at module load. */
const PRESET_CLASS = Object.fromEntries(
  (Object.keys(BUTTON_COLOR_CLASS) as IconButtonVariant[]).flatMap((variant) =>
    (["neutral", "danger"] as const).flatMap((tone) =>
      (Object.keys(SIZE_CLASS) as IconButtonSize[]).map((size) => [
        `${variant}.${tone}.${size}`,
        cn(BUTTON_INTERACTION_CLASS, SHAPE_CLASS, BUTTON_COLOR_CLASS[variant][tone], SIZE_CLASS[size]),
      ]),
    ),
  ),
) as Record<`${IconButtonVariant}.${ButtonTone}.${IconButtonSize}`, string>

/**
 * The full IconButton class string — for a non-`<button>` element (a link)
 * that has to look like one, and used by `IconButton` itself.
 */
export function iconButtonClassName({
  variant = "borderless",
  tone = "neutral",
  size = "md",
  className,
}: {
  variant?: IconButtonVariant
  tone?: ButtonTone
  size?: IconButtonSize
  className?: string
} = {}) {
  const preset = PRESET_CLASS[`${variant}.${tone}.${size}`]
  return className ? cn(preset, className) : preset
}

/**
 * The label-less member of the button system: `Button`'s colours, timing,
 * 1px settle and 42% fade, drawn as a circle so it sits beside a pill without
 * a corner of its own.
 */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    { variant = "borderless", tone = "neutral", size = "md", active, className, type = "button", ...rest },
    ref,
  ) => (
    <button
      ref={ref}
      type={type}
      aria-pressed={active}
      data-active={active ? "true" : undefined}
      className={iconButtonClassName({ variant, tone, size, className })}
      {...rest}
    />
  ),
)

IconButton.displayName = "IconButton"
